/**
 * @fileoverview Confirm modal for Migrate Entire Department.
 * @module system-web/components/migration/DepartmentWideConfirmModal
 *
 * Responsibilities:
 * - List frozen occupancy adapters before a global apply/dry-run.
 *
 * Architecture: presentation-only. Parent owns submit via legacyMigrationService.startRun.
 *
 * @component DepartmentWideConfirmModal
 * Purpose: Warn before department-wide occupancy migration.
 * Props: mode, adapters, isSubmitting, error, onConfirm, onCancel
 * Behavior: Confirm posts datasetKey department-wide with selection ['*'].
 * Data Source: adapter names from parent (ADAPTER_META / fetchDatasets)
 * Constraints: no fabricated record counts
 * Performance: N/A
 */

import React from 'react';
import { AlertTriangle, Loader2, XCircle } from 'lucide-react';

export interface DepartmentWideAdapterLine {
  key: string;
  displayName: string;
  count?: number;
}

export function DepartmentWideConfirmModal({
  mode,
  adapters,
  estimateAvailable,
  estimateError,
  estimateLoading,
  isSubmitting,
  error,
  onConfirm,
  onCancel,
}: {
  mode: 'dry-run' | 'apply';
  adapters: DepartmentWideAdapterLine[];
  estimateAvailable?: boolean;
  estimateError?: string | null;
  estimateLoading?: boolean;
  isSubmitting: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
      <div className="bg-white rounded-xl shadow-xl p-6 max-w-lg w-full mx-4 max-h-[80vh] flex flex-col">
        <h2 className="text-lg font-semibold mb-2">
          {mode === 'dry-run' ? 'Preview entire department' : 'Migrate entire department'}
        </h2>
        <p className="text-sm text-gray-600 mb-3">
          {mode === 'dry-run'
            ? 'Dry-run every occupancy dataset in order. No tenant data is written. Blob copy is not started.'
            : 'This writes occupancies, contacts, systems, tanks, chemicals, hydrants, and document metadata for the whole department. File bytes transfer in the background after Documents. Re-running upserts; it does not undo.'}
        </p>
        <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 mt-0.5" />
          <span>Each step uses selection *. Grid checkboxes on the current tab are ignored.</span>
        </div>
        {estimateLoading && (
          <p className="text-xs text-gray-500 mb-2 inline-flex items-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" /> Loading row counts…
          </p>
        )}
        {estimateAvailable === false && (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-2">
            {estimateError || 'Legacy SQL not available'}
          </p>
        )}
        <ul className="text-xs text-gray-700 border rounded-lg divide-y divide-gray-100 overflow-y-auto mb-4 flex-1 min-h-0">
          {adapters.map((a) => (
            <li key={a.key} className="px-3 py-1.5 flex items-center justify-between gap-2">
              <span>{a.displayName}</span>
              <span className="tabular-nums text-gray-500">
                {typeof a.count === 'number' ? a.count.toLocaleString() : estimateAvailable === false ? '—' : ''}
              </span>
            </li>
          ))}
        </ul>
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 mb-4">
            <XCircle className="h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}
        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50"
          >
            {isSubmitting ? 'Close' : 'Cancel'}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isSubmitting}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-orange-600 hover:bg-orange-700 rounded-lg disabled:opacity-50"
          >
            {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {mode === 'dry-run' ? 'Run preview' : 'Migrate department'}
          </button>
        </div>
      </div>
    </div>
  );
}
